import React from 'react';
import { Drama, Users, Target, Award, BookOpen, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Smashed = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/3830745/pexels-photo-3830745.jpeg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/85 to-purple-800/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Drama className="h-20 w-20 mx-auto mb-6 animate-pulse" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">SMASHED Project</h1>
          <p className="text-2xl mb-2">Theatre-in-Education Programme</p>
          <p className="text-xl">Addressing teenage drinking through powerful storytelling</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Educating Through Performance</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
            Developed by Collingwood Education in the UK, SMASHED Project is an internationally-acclaimed theatre-in-education programme that addresses the social dangers of teenage drinking and seeks to reduce alcohol-related harm in young people.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
            In New Zealand, SMASHED is delivered by <strong>Life Education Trust</strong> and reaches over <strong>18,000 Year 9 students every year</strong>, beginning in 2019.
          </p>
        </div>

        {/* Video Section */}
        <Card className="mb-12 bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Watch SMASHED in Action</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-xl">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/xChVKZ1fqJ8"
                title="SMASHED Project Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
            <div className="mt-4 text-center">
              <a 
                href="https://www.youtube.com/watch?v=xChVKZ1fqJ8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold transition-colors"
              >
                Watch on YouTube
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* What is SMASHED */}
        <Card className="mb-12 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <BookOpen className="mr-3 h-8 w-8 text-indigo-600" />
              What is SMASHED?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              SMASHED is a dynamic, interactive theatre performance that brings the realities of underage drinking to life for secondary school students. Through compelling storytelling and relatable characters, the programme explores the consequences of alcohol use in an engaging and age-appropriate way.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The performance is followed by facilitated discussions where students can explore the themes raised and discuss their own attitudes and experiences around alcohol in a safe, supportive environment.
            </p>
          </CardContent>
        </Card>

        {/* Key Features */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Programme Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Users className="mr-3 h-8 w-8" />
                  Interactive Theatre
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed">
                  Professional actors deliver a high-energy, interactive performance that captivates students and brings the issues to life in a memorable way.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Target className="mr-3 h-8 w-8" />
                  Age-Appropriate Content
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed">
                  The programme is specifically designed for Year 9 students, addressing the challenges they face during this critical period.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader className="bg-gradient-to-br from-pink-500 to-rose-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <BookOpen className="mr-3 h-8 w-8" />
                  Evidence-Based Approach
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed">
                  The programme is grounded in research about teenage drinking behaviors and effective prevention strategies, ensuring maximum impact.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardHeader className="bg-gradient-to-br from-rose-500 to-red-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Award className="mr-3 h-8 w-8" />
                  Proven Results
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed">
                  SMASHED has been delivered to thousands of students internationally, with proven effectiveness in changing attitudes toward underage drinking.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What Students Learn */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">What Students Learn</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-6">
              Through the SMASHED programme, students gain understanding and skills in several key areas:
            </p>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-indigo-100 to-purple-100 p-6 rounded-lg border-l-4 border-indigo-500">
                <h3 className="font-bold text-gray-900 mb-2">Understanding the Risks</h3>
                <p className="text-gray-700">
                  Students learn about the physical, social, and emotional risks associated with underage drinking in a way that resonates with their experiences.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="font-bold text-gray-900 mb-2">Peer Pressure Resistance</h3>
                <p className="text-gray-700">
                  The programme provides strategies for dealing with peer pressure and making independent choices about alcohol.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-pink-100 to-rose-100 p-6 rounded-lg border-l-4 border-pink-500">
                <h3 className="font-bold text-gray-900 mb-2">Decision-Making Skills</h3>
                <p className="text-gray-700">
                  Students develop critical thinking skills to help them make informed decisions about alcohol use.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-rose-100 to-red-100 p-6 rounded-lg border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Supporting Friends</h3>
                <p className="text-gray-700">
                  The programme teaches students how to look out for friends and respond appropriately if someone is in trouble.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Zealand Impact Section */}
        <Card className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50">
          <CardHeader>
            <CardTitle className="text-2xl">Impact in New Zealand</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-6">
              Since beginning in 2019, SMASHED has made a significant impact on young people's attitudes toward alcohol in New Zealand:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-4xl font-bold text-indigo-600 mb-2">18,000+</p>
                <p className="text-gray-700 font-semibold">Year 9 Students Reached Annually</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-4xl font-bold text-purple-600 mb-2">2019</p>
                <p className="text-gray-700 font-semibold">Programme Started in NZ</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-4xl font-bold text-pink-600 mb-2">100+</p>
                <p className="text-gray-700 font-semibold">Schools Participated</p>
              </div>
            </div>
            <div className="mt-6 bg-white p-6 rounded-lg">
              <p className="text-gray-700 text-center">
                <strong>Delivered by Life Education Trust</strong> - New Zealand's leading provider of health education programmes for children and young people.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* For Schools */}
        <Card className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4 text-center">For Schools and Educators</h3>
            <p className="text-lg mb-6 text-center">
              SMASHED Project offers a powerful tool for addressing alcohol education in an engaging and effective way. The programme aligns with health curriculum requirements and provides a memorable experience that sparks important conversations.
            </p>
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-lg">
              <h4 className="text-xl font-bold mb-3">Programme Includes:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-yellow-300 mr-2">✓</span>
                  <span>60-minute interactive theatre performance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-300 mr-2">✓</span>
                  <span>Post-show facilitated discussions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-300 mr-2">✓</span>
                  <span>Teacher resource pack</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-300 mr-2">✓</span>
                  <span>Follow-up classroom activities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-300 mr-2">✓</span>
                  <span>Parent information materials</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Smashed;